import type {
  ContactExpediente,
  ContactExpedienteDocument,
  ContactExpedienteInput,
} from "../../types";
import {
  computeExpedienteMissing,
  isValidIsoDate,
  validateExpedienteBirthDate,
  validateExpedienteCurp,
  validateExpedienteIdExpiry,
  validateExpedienteRfc,
  validateExpedienteZip,
} from "../../providers/commons/contactExpediente";

/** Inline message per field (Spanish, never echoing the entered value). */
export type ExpedienteFieldMessages = Partial<
  Record<keyof ContactExpedienteInput, string>
>;

/** Spanish texts of the three "required once you try to save" rules (same as the SQL). */
export type ExpedienteRequiredTexts = {
  kind: string;
  regime: string;
  ownerName: string;
  idExpired: string;
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === "string" && value.trim() === "");

/**
 * Validates the form with the TASK-124 validators. `errors` block saving; `warnings` (an expired
 * ID, which is allowed to be saved but counts as missing) never do. The three required-when rules
 * (kind, regime when casado, owner name) only show once `attempted` is true.
 */
export const validateExpedienteForm = (
  values: ContactExpedienteInput,
  attempted: boolean,
  texts: ExpedienteRequiredTexts,
): { errors: ExpedienteFieldMessages; warnings: ExpedienteFieldMessages } => {
  const errors: ExpedienteFieldMessages = {};
  const warnings: ExpedienteFieldMessages = {};
  const kind = values.person_kind;

  const checks: Array<[keyof ContactExpedienteInput, string | undefined]> = [
    ["rfc", validateExpedienteRfc(values.rfc, kind)],
    ["curp", validateExpedienteCurp(values.curp, kind)],
    ["address_zip", validateExpedienteZip(values.address_zip)],
    ["birth_date", validateExpedienteBirthDate(values.birth_date)],
    ["id_expires_on", validateExpedienteIdExpiry(values.id_expires_on)],
  ];
  for (const [field, message] of checks) {
    if (message) errors[field] = message;
  }

  if (
    !errors.id_expires_on &&
    values.id_expires_on &&
    isValidIsoDate(values.id_expires_on) &&
    values.id_expires_on < todayIso()
  ) {
    warnings.id_expires_on = texts.idExpired;
  }

  if (attempted) {
    if (!kind) errors.person_kind = texts.kind;
    if (kind !== "persona_moral") {
      if (
        values.marital_status === "casado" &&
        !values.marital_property_regime
      ) {
        errors.marital_property_regime = texts.regime;
      }
    }
    if (
      values.beneficial_owner_is_self === false &&
      isBlank(values.beneficial_owner_name)
    ) {
      errors.beneficial_owner_name = texts.ownerName;
    }
  }
  return { errors, warnings };
};

const FISICA_ONLY_FIELDS: ReadonlyArray<string> = [
  "birth_date",
  "birth_place",
  "nationality",
  "occupation",
  "marital_status",
  "marital_property_regime",
  "address_street",
  "address_number",
  "address_neighborhood",
  "address_zip",
  "address_municipality",
  "address_state",
  "id_type",
  "id_expires_on",
];

const MORAL_ONLY_FIELDS: ReadonlyArray<string> = [
  "legal_name",
  "legal_representative_name",
];

/**
 * The values a save sends: only the fields of the selected kind. A persona moral never carries a
 * CURP (the SQL rejects it, so it is nulled) nor the hidden fisica-only fields; a persona fisica
 * never carries the hidden moral-only fields.
 */
export const buildExpedienteSavePayload = (
  values: ContactExpedienteInput,
): ContactExpedienteInput => {
  const isMoral = values.person_kind === "persona_moral";
  const hidden = isMoral
    ? FISICA_ONLY_FIELDS
    : values.person_kind === "persona_fisica"
      ? MORAL_ONLY_FIELDS
      : [];
  const kept = Object.fromEntries(
    Object.entries(values).filter(([field]) => !hidden.includes(field)),
  ) as ContactExpedienteInput;
  return isMoral ? { ...kept, curp: null } : kept;
};

/** The progress "n de m": how many checklist items are done out of the total for this kind. */
export const computeExpedienteProgress = (
  expediente: Partial<ContactExpediente> | null | undefined,
  documents: ReadonlyArray<
    Pick<ContactExpedienteDocument, "doc_type" | "deleted_at">
  >,
): { done: number; total: number; missing: string[] } => {
  const missing = computeExpedienteMissing(expediente, documents);
  const kind = expediente?.person_kind ?? "persona_fisica";
  // Everything missing for an empty file of this kind, minus the "no file yet" label.
  const baseTotal = computeExpedienteMissing(
    { person_kind: kind },
    [],
    todayIso(),
  ).length;
  const extra =
    (kind === "persona_fisica" && expediente?.marital_status === "casado"
      ? 1
      : 0) + (expediente?.is_pep === true ? 1 : 0);
  const total = Math.max(baseTotal + extra, missing.length);
  return { done: total - missing.length, total, missing };
};
